import { logger } from '../utils/logger.js'
import { ContextSyncService } from './context-sync.js'
import { createContextSyncApi } from './context-sync-api.js'
import { pushPendingChanges } from './sync-push-handler.js'
import { authService } from '../services/auth/auth-service.js'
import { showToast } from '../components/common/cairn-toast.js'
import { db } from '../database/schema.js'
import { environment } from '../environments/environment.prod.js'
import { STORAGE_KEYS } from '../services/layer-preferences-storage.js'
import { edgeService, nodeRef } from '../services/edge-service.js'
import { SettingsStorage } from './settings-storage.js'
import { DEVICE_LOCAL_SETTINGS_KEYS } from './user-storage.js'
import type { UserSettings } from '../schemas/user.js'

const log = logger.create('sync-manager')

/**
 * Mirror the account-level fields of the synced `users.settings` into the localStorage
 * settings store — the synchronous read source for unit/theme/coordinate consumers.
 * Layer-only fields (layers/layerOrder/baseLayer) are handled by the layer store, and
 * device-local keys (e.g. syncPreferences) are preserved from the existing local copy.
 */
function mirrorSyncedSettingsToLocal(serverSettings: Record<string, unknown>): void {
  const store = new SettingsStorage()
  const local = store.getSettingsWithDefaults() as unknown as Record<string, unknown>
  const { layers: _l, layerOrder: _o, baseLayer: _b, ...accountLevel } = serverSettings
  void _l
  void _o
  void _b
  const merged: Record<string, unknown> = { ...local, ...accountLevel }
  for (const k of DEVICE_LOCAL_SETTINGS_KEYS) merged[k] = local[k]
  store.saveSettings(merged as unknown as UserSettings)
}

let syncService: ContextSyncService | null = null
let backgroundInterval: ReturnType<typeof setInterval> | null = null
let currentContext: { type: string; id?: string } | null = null
export { schedulePush } from './sync-push-scheduler.js'

function getApi() {
  const baseUrl = environment.api.baseUrl
  return createContextSyncApi(baseUrl, async () => {
    const token = authService.getAccessToken()
    if (!token) throw new Error('Not authenticated')
    return token
  })
}

function getSyncService(): ContextSyncService {
  if (!syncService) {
    syncService = new ContextSyncService(getApi())
  }
  return syncService
}

export async function syncListOnNavigate(entityType: string): Promise<void> {
  if (!authService.getAccessToken()) {
    log.debug('Skipping sync: not authenticated')
    return
  }

  currentContext = { type: entityType }

  const pendingEvents = await db.syncEvents.filter(e => !e.syncedAt).count()
  log.debug(`Sync starting: ${pendingEvents} pending events, entityType=${entityType}`)

  try {
    const pushResult = await pushPendingChanges(getApi())
    log.debug('Push result:', pushResult)
    if (!pushResult.success) {
      showToast('error', `Sync push failed: ${pushResult.errors.join(', ')}`)
    } else if (pushResult.pushed > 0) {
      showToast('success', `Synced ${pushResult.pushed} change${pushResult.pushed > 1 ? 's' : ''}`)
    }
    if (pushResult.denied.length > 0) {
      showToast('error', `Access denied for ${pushResult.denied.length} item(s)`)
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    log.error('Push failed:', err)
    showToast('error', `Sync push error: ${msg}`)
  }

  try {
    const syncResult = await getSyncService().syncList(entityType)
    log.debug('Sync list result:', syncResult)
    if (!syncResult.success) {
      showToast('error', `Sync pull failed: ${syncResult.error}`)
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    log.error('Sync list failed:', err)
    showToast('error', `Sync pull error: ${msg}`)
  }
}

export async function pullChildren(parentType: string, parentId: string): Promise<void> {
  if (!authService.getAccessToken()) return

  currentContext = { type: parentType, id: parentId }

  try {
    const syncResult = await getSyncService().syncChildren(parentType, parentId)
    if (!syncResult.success) {
      showToast('error', `Sync pull failed: ${syncResult.error}`)
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    log.error('Sync children failed:', err)
    showToast('error', `Sync pull error: ${msg}`)
  }
}

export async function syncChildrenOnNavigate(parentType: string, parentId: string): Promise<void> {
  if (!authService.getAccessToken()) return

  currentContext = { type: parentType, id: parentId }

  try {
    const pushResult = await pushPendingChanges(getApi())
    if (!pushResult.success) {
      showToast('error', `Sync push failed: ${pushResult.errors.join(', ')}`)
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    log.error('Push failed:', err)
    showToast('error', `Sync push error: ${msg}`)
  }

  try {
    const syncResult = await getSyncService().syncChildren(parentType, parentId)
    if (!syncResult.success) {
      showToast('error', `Sync pull failed: ${syncResult.error}`)
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    log.error('Sync children failed:', err)
    showToast('error', `Sync pull error: ${msg}`)
  }
}

export function startBackgroundSync(intervalMs = 30000): void {
  stopBackgroundSync()
  backgroundInterval = setInterval(async () => {
    if (!authService.getAccessToken() || !currentContext) return

    try {
      await pushPendingChanges(getApi())
    } catch (err) {
      log.error('Background push failed:', err)
    }

    try {
      if (currentContext.id) {
        await getSyncService().syncChildren(currentContext.type, currentContext.id)
      } else {
        await getSyncService().syncList(currentContext.type)
      }
    } catch (err) {
      log.error('Background sync failed:', err)
    }
  }, intervalMs)
}

export function stopBackgroundSync(): void {
  if (backgroundInterval) {
    clearInterval(backgroundInterval)
    backgroundInterval = null
  }
}

export async function syncUserSettings(): Promise<void> {
  if (!authService.getAccessToken()) return

  try {
    await pushPendingChanges(getApi())
  } catch (err) {
    log.error('Push failed before user settings pull:', err)
  }

  try {
    const result = await getSyncService().syncList('user')
    if (!result.success) {
      log.warn('User settings pull failed:', result.error)
      return
    }

    const userId = authService.getState().userId
    if (!userId) return

    const user = await db.users.get(userId)
    if (!user) return

    const settings = user.settings as Record<string, unknown> | undefined

    // Mirror account-level settings (units/theme/coords/…) into the localStorage read
    // store so the synchronous consumers pick them up, and notify listeners to re-init
    // cached readers (theme, map state).
    if (settings) {
      mirrorSyncedSettingsToLocal(settings)
      window.dispatchEvent(new CustomEvent('user-settings-synced', { detail: { settings } }))
    }

    // Unified per-layer state is authoritative when present. Persist it locally and
    // dispatch a REPLACE event (the listener overwrites the store, so off-states and
    // removals from another device propagate — unlike the old union merge). Carries the
    // synced layer order + base map alongside.
    const serverLayers = settings?.layers as unknown[] | undefined
    if (serverLayers) {
      localStorage.setItem(STORAGE_KEYS.LAYER_STATE, JSON.stringify(serverLayers))
      log.info(`Restored ${serverLayers.length} layer states from server`)
      window.dispatchEvent(
        new CustomEvent('layer-settings-synced', {
          detail: {
            layers: serverLayers,
            layerOrder: settings?.layerOrder as Record<string, number> | undefined,
            baseLayer: settings?.baseLayer as string | undefined,
          },
        })
      )
      return
    }

    // Legacy fallback: account predates `layers` — surface favorites/panelLayers so
    // the client migrates them into the unified store (then future pushes use it).
    const serverFavorites = settings?.favoriteLayers as string[] | undefined
    if (serverFavorites && serverFavorites.length > 0) {
      localStorage.setItem(STORAGE_KEYS.FAVORITE_LAYERS, JSON.stringify(serverFavorites))
    }
    const serverPanelLayers = settings?.panelLayers as unknown[] | undefined
    if (serverPanelLayers && serverPanelLayers.length > 0) {
      localStorage.setItem(STORAGE_KEYS.PANEL_LAYERS, JSON.stringify(serverPanelLayers))
    }
    if (serverFavorites || serverPanelLayers) {
      window.dispatchEvent(
        new CustomEvent('layer-settings-synced', {
          detail: {
            favoriteLayers: serverFavorites ?? [],
            panelLayers: serverPanelLayers ?? [],
          },
        })
      )
    }
  } catch (err) {
    log.error('User settings sync failed:', err)
  }
}

export async function forcePushEntity(
  entityType: string,
  entityId: string
): Promise<{ pushed: number; errors: string[] }> {
  if (!authService.getAccessToken()) {
    return { pushed: 0, errors: ['Not authenticated'] }
  }

  const tableName =
    entityType === 'activity'
      ? 'activities'
      : entityType === 'route'
        ? 'routes'
        : entityType === 'plan'
          ? 'plans'
          : null
  if (!tableName) {
    return { pushed: 0, errors: [`Unknown entity type: ${entityType}`] }
  }

  const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]
  const entity = await table.get(entityId)
  if (!entity) {
    showToast('error', 'Entity not found locally')
    return { pushed: 0, errors: ['Entity not found'] }
  }

  const api = getApi()
  const changes: import('./context-sync-api.js').PushChange[] = []
  const entityData = { ...(entity as Record<string, unknown>) }
  const entityVersion = (entityData.version as number) ?? 0
  delete entityData.version

  changes.push({
    syncEventId: crypto.randomUUID(),
    entityType,
    entityId,
    operation: entityVersion > 0 ? 'update' : 'create',
    data: entityData,
    expectedVersion: entityVersion,
    force: true,
  })

  const entityRef = nodeRef(entityType, entityId)
  const groupIds = await edgeService.getObjectIds(entityRef, 'has-layer-group')

  for (const groupId of groupIds) {
    const groupRef = nodeRef('group', groupId)

    // Push the layer group entity itself
    const group = await db.layerGroups.get(groupId)
    if (group) {
      const groupData = { ...(group as unknown as Record<string, unknown>) }
      const groupVersion = (groupData.version as number) ?? 0
      delete groupData.version
      changes.push({
        syncEventId: crypto.randomUUID(),
        entityType: 'layerGroup',
        entityId: groupId,
        operation: groupVersion > 0 ? 'update' : 'create',
        data: groupData,
        expectedVersion: groupVersion,
        force: true,
      })
    }

    // Push the has-layer-group edge (filter to the specific group)
    const lgEdge = await db.edges
      .where({ subject: entityRef, predicate: 'has-layer-group' })
      .filter(e => e.object === groupRef)
      .first()
    if (lgEdge) {
      changes.push({
        syncEventId: crypto.randomUUID(),
        entityType: 'edge',
        entityId: lgEdge.id,
        operation: 'create',
        data: {
          subject: entityRef,
          predicate: 'has-layer-group',
          object: groupRef,
          properties: lgEdge.properties ?? {},
        },
        expectedVersion: 0,
        force: true,
      })
    }

    const featureIds = await edgeService.getObjectIds(groupRef, 'has-feature')
    for (const featureId of featureIds) {
      const feature = await db.features.get(featureId)
      if (feature) {
        const featureData = { ...(feature as unknown as Record<string, unknown>) }
        const featureVersion = (featureData.version as number) ?? 0
        delete featureData.version
        changes.push({
          syncEventId: crypto.randomUUID(),
          entityType: 'feature',
          entityId: featureId,
          operation: featureVersion > 0 ? 'update' : 'create',
          data: featureData,
          expectedVersion: featureVersion,
          force: true,
        })
      }

      const featureEdge = await db.edges
        .where({ subject: groupRef, predicate: 'has-feature' })
        .filter(e => e.object === nodeRef('feature', featureId))
        .first()
      if (featureEdge) {
        changes.push({
          syncEventId: crypto.randomUUID(),
          entityType: 'edge',
          entityId: featureEdge.id,
          operation: 'create',
          data: {
            subject: groupRef,
            predicate: 'has-feature',
            object: nodeRef('feature', featureId),
            properties: featureEdge.properties ?? {},
          },
          expectedVersion: 0,
          force: true,
        })
      }
    }
  }

  log.info(`Force push: sending ${changes.length} items for ${entityType}/${entityId}`)

  try {
    const response = await api.push({ changes })
    const succeeded = response.results.filter(r => r.success).length
    const failed = response.results.filter(r => !r.success)

    if (failed.length > 0) {
      log.warn(`Force push: ${failed.length} items failed`, failed)
    }

    if (succeeded > 0) {
      showToast('success', `Pushed ${succeeded} item${succeeded > 1 ? 's' : ''}`)
    } else {
      showToast('error', 'Push failed — check logs')
    }

    return { pushed: succeeded, errors: failed.map(f => f.error ?? 'unknown') }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    showToast('error', `Push error: ${msg}`)
    return { pushed: 0, errors: [msg] }
  }
}

export function resetSyncService(): void {
  syncService = null
  currentContext = null
}
